import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../services/api';
import GroupedAccountSelect from './GroupedAccountSelect';
import CategorizationConflictModal from './CategorizationConflictModal';

// Set up PDF.js worker - use local worker file
if (typeof window !== 'undefined') {
  // Use the local worker file we copied to public directory
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
}

interface PDFImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  accounts: Array<{ id: number; name: string; institution: string; type: string }>;
  onLoadAccounts?: () => void;
}

interface ParsedTransaction {
  date: string;
  description: string;
  amount: number;
  reference?: string;
  category?: string;
  confidence: number;
}

interface ExtractionResult {
  text: string;
  isScanned: boolean;
  confidence: number;
  transactionCount: number;
}

interface QCResult {
  expected_count: number | null;
  parsed_count: number;
  discrepancy: number | null;
  warnings: string[];
  confidence: 'high' | 'medium' | 'low';
}

interface DuplicateMatch {
  date: string;   // YYYY-MM-DD
  amount: number;
  description: string;
}

const PDFImportModal: React.FC<PDFImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  accounts,
  onLoadAccounts
}) => {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [parsedTransactions, setParsedTransactions] = useState<ParsedTransaction[]>([]);
  const [qcResult, setQcResult] = useState<QCResult | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<{duplicates: any[], willImport: number, willSkip: number, matchMap: Record<number, DuplicateMatch>} | null>(null);
  const [overriddenIndices, setOverriddenIndices] = useState<Set<number>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Conflict resolution state
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [scale, setScale] = useState(1.2);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load accounts and categories when modal opens
  useEffect(() => {
    if (isOpen && accounts.length === 0 && onLoadAccounts) {
      onLoadAccounts();
    }
    if (isOpen && categories.length === 0) {
      loadCategories();
    }
  }, [isOpen, accounts.length, onLoadAccounts]);

  const loadCategories = async () => {
    try {
      const categoriesData = await api.transactions.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };



  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'application/pdf' && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    setPdfFile(selectedFile);
    setError(null);
    setSuccess(null);
    setExtractionResult(null);
    setParsedTransactions([]);
    setQcResult(null);
    setDuplicateInfo(null);
    
    // Create data URL for PDF viewer
    const reader = new FileReader();
    reader.onload = (e) => {
      setPdfDataUrl(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF loading error:', error);
    setError('Failed to load PDF for preview');
  };

  // Helper function to normalize dates for comparison.
  // Slices to the first 10 characters (YYYY-MM-DD) to avoid timezone shifts
  // that occur when new Date() parses ISO date-only strings as UTC midnight.
  const normalizeDate = (dateString: string): string => {
    if (!dateString) return dateString;
    return String(dateString).slice(0, 10);
  };

  const checkForDuplicates = async (transactions: ParsedTransaction[]) => {
    if (!selectedAccountId || transactions.length === 0) return;

    try {
      // Get existing transactions for the account
      // Note: We need to check by linked_account_id, not manual account ID
      const { transactions: existingTransactions } = await api.transactions.getTransactions({
        accountId: selectedAccountId
      });

      // If this is a Plaid account, also try to get transactions by linked_account_id
      const selectedAccount = accounts?.find((acc: any) => acc.id === selectedAccountId);
      let plaidTransactions: any[] = [];
      if (selectedAccount && (selectedAccount as any).linked_account_id) {
        try {
          const { transactions: linked } = await api.transactions.getTransactions({
            accountId: (selectedAccount as any).linked_account_id
          });
          plaidTransactions = linked;
        } catch (error) {
          // linked account lookup is best-effort
        }
      }

      // If no transactions found with accountId, try to find the linked account
      let transactionsToCheck = existingTransactions;
      if (existingTransactions.length === 0 && plaidTransactions.length > 0) {
        transactionsToCheck = plaidTransactions;
      } else if (existingTransactions.length === 0) {
        const { transactions: allTransactions } = await api.transactions.getTransactions({});

        // Check if any transactions have the account ID we're looking for
        const transactionsWithOurAccountId = allTransactions.filter((trans: any) =>
          trans.account_id === selectedAccountId || trans.linked_account_id === selectedAccountId
        );
        if (transactionsWithOurAccountId.length > 0) {
          transactionsToCheck = transactionsWithOurAccountId;
        }

        // If we found account-specific transactions, use those. Otherwise, filter all transactions
        if (transactionsToCheck.length === 0) {
          transactionsToCheck = allTransactions.filter((existing: any) => {
            return transactions.some((newTrans: any) => {
              const sameDate = normalizeDate(existing.date) === normalizeDate(newTrans.date);
              const sameAmount = Math.abs(Math.abs(existing.amount) - Math.abs(newTrans.amount)) < 0.01;
              const sameDescription = existing.description.toLowerCase().trim() === newTrans.description.toLowerCase().trim();
              return sameDate && sameAmount && sameDescription;
            });
          });
        }
      }

      const duplicates: any[] = [];
      const willImport: ParsedTransaction[] = [];
      const matchMap: Record<number, DuplicateMatch> = {};

      for (const [index, transaction] of transactions.entries()) {
        // Find the first existing transaction that matches by date + absolute amount.
        // Using find (not some) so we can capture the match details for display.
        const matchingExisting = transactionsToCheck.find((existing: any) => {
          const sameDate = normalizeDate(existing.date) === normalizeDate(transaction.date);
          // Compare absolute values so sign differences (Plaid positive vs PDF negative) don't cause misses.
          const sameAmount = Math.abs(Math.abs(existing.amount) - Math.abs(transaction.amount)) < 0.01;
          return sameDate && sameAmount;
        });

        if (matchingExisting) {
          duplicates.push(transaction);
          matchMap[index] = {
            date: normalizeDate(matchingExisting.date),
            amount: matchingExisting.amount,
            description: matchingExisting.description ?? '',
          };
        } else {
          willImport.push(transaction);
        }
      }

      setDuplicateInfo({
        duplicates,
        willImport: willImport.length,
        willSkip: duplicates.length,
        matchMap,
      });
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      console.error('Error details:', error);
      // If we can't check duplicates, assume all will be imported
      setDuplicateInfo({
        duplicates: [],
        willImport: transactions.length,
        willSkip: 0,
        matchMap: {},
      });
    }
  };

  const handlePreview = async () => {
    if (!pdfFile || !selectedAccountId) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await api.transactions.importTransactionsPDF({
        manual_account_id: selectedAccountId,
        file: pdfFile,
        preview_only: true
      });

      setExtractionResult(result.extraction);
      setParsedTransactions(result.transactions);
      setQcResult(result.qc || null);
      setOverriddenIndices(new Set());

      // Check for duplicates after parsing
      await checkForDuplicates(result.transactions);
    } catch (err: any) {
      setError(err.message || 'Failed to process PDF');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!pdfFile || !selectedAccountId) return;

    setIsImporting(true);
    setError(null);

    try {
      const result = await api.transactions.importTransactionsPDF({
        manual_account_id: selectedAccountId,
        file: pdfFile
      });

      setSuccess(result.message || 'PDF imported successfully!');
      console.log('PDF import result:', result);
      
      // Log detailed error information for debugging
      if (result.errors && result.errors.length > 0) {
        console.log('PDF import errors:', result.errors);
        result.errors.forEach((error: string, index: number) => {
          console.log(`Error ${index + 1}:`, error);
        });
      }
      
      // Check for categorization conflicts
      if (result.categorization?.has_conflicts && result.categorization.cross_type_conflicts?.length > 0) {
        console.log('Found categorization conflicts:', result.categorization.cross_type_conflicts);
        setConflicts(result.categorization.cross_type_conflicts);
        setConflictModalOpen(true);
        // Don't call onImportComplete yet - wait for conflict resolution
      } else {
        console.log('Calling onImportComplete...');
        onImportComplete();
      }
      
      // Reset form
      setPdfFile(null);
      setSelectedAccountId(null);
      setExtractionResult(null);
      setParsedTransactions([]);
      setQcResult(null);
      setDuplicateInfo(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      console.error('PDF import error:', err);
      setError(err.message || 'Failed to import PDF');
    } finally {
      setIsImporting(false);
    }
  };

  const handleConflictResolve = async (action: 'apply_rules' | 'categorize_manually' | 'leave_uncategorized' | 'mark_as_refunds', categoryId?: number, subcategoryId?: number) => {
    try {
      const transactionIds = conflicts.map(c => c.transaction.id);
      await api.transactions.resolveCategorizationConflicts({
        transactionIds,
        action,
        categoryId,
        subcategoryId,
        conflicts
      });
      
      setConflictModalOpen(false);
      setConflicts([]);
      console.log('Calling onImportComplete after conflict resolution...');
      // Add a delay to ensure database transaction is committed and categories are reloaded
      setTimeout(() => {
        console.log('Executing onImportComplete after delay...');
        onImportComplete();
      }, 1000);
    } catch (error) {
      console.error('Error resolving conflicts:', error);
      setError('Failed to resolve categorization conflicts');
    }
  };

  const handleClose = () => {
    setPdfFile(null);
    setSelectedAccountId(null);
    setExtractionResult(null);
    setParsedTransactions([]);
    setQcResult(null);
    setDuplicateInfo(null);
    setOverriddenIndices(new Set());
    setError(null);
    setSuccess(null);
    setPdfDataUrl(null);
    setNumPages(null);
    setPageNumber(1);
    setScale(1.2);
    setConflictModalOpen(false);
    setConflicts([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Format a YYYY-MM-DD string as "MMM D, YYYY" without using new Date() (avoids timezone shift).
  const formatMatchDate = (dateStr: string): string => {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${months[month - 1]} ${day}, ${year}`;
  };

  const effectiveImportCount = duplicateInfo
    ? duplicateInfo.willImport + overriddenIndices.size
    : parsedTransactions.length;

  const effectiveSkipCount = duplicateInfo
    ? duplicateInfo.willSkip - overriddenIndices.size
    : 0;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content pdf-import-modal">
        <div className="modal-header">
          <h2>📄 Import PDF Statement</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {success && (
            <div className="success-message" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span className="success-icon">✅</span>
              <span style={{ flex: 1 }}>{success}</span>
              <button
                onClick={onImportComplete}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-color, #7c3aed)', cursor: 'pointer', fontSize: '12px', fontWeight: 500, textDecoration: 'underline', flexShrink: 0 }}
              >
                Categorize now →
              </button>
            </div>
          )}

          <div className="import-section">
            <h3>1. Select Account</h3>
            <GroupedAccountSelect
              accounts={accounts}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
              placeholder="Choose an account..."
              className="form-select"
            />
            
            {accounts.length === 0 && (
              <div className="account-help">
                <p>📝 <strong>No accounts found!</strong></p>
                <p>You need to create a manual account first before importing PDF transactions.</p>
                <div className="help-steps">
                  <div className="help-step">
                    <span>1.</span> Go to <strong>Assets</strong> or <strong>Liabilities</strong> page
                  </div>
                  <div className="help-step">
                    <span>2.</span> Click <strong>"Add Account"</strong> 
                  </div>
                  <div className="help-step">
                    <span>3.</span> Create your <strong>ScotiaBank Visa</strong> account
                  </div>
                  <div className="help-step">
                    <span>4.</span> Come back here to import transactions
                  </div>
                </div>
              </div>
            )}
            
            {accounts.length > 0 && !selectedAccountId && (
              <div className="account-help small">
                <p>💡 Select the account where these PDF transactions should be imported</p>
              </div>
            )}
            
            {selectedAccountId && !pdfFile && (
              <div className="account-help small">
                <p>📄 Upload a PDF file to continue</p>
              </div>
            )}
          </div>

          <div className="import-section">
            <h3>2. Upload PDF Statement</h3>
            <div className="file-upload">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="file-input"
              />
              <div className="upload-area">
                <div className="upload-icon">📄</div>
                <p>Click to select PDF file or drag and drop</p>
                <p className="upload-hint">Supports TD Visa and other bank statement PDFs</p>
              </div>
            </div>

            {pdfFile && (
              <div className="file-info">
                <p><strong>Selected file:</strong> {pdfFile.name}</p>
                <p><strong>Size:</strong> {(pdfFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            )}
          </div>

          {pdfFile && selectedAccountId && (
            <div className="import-section">
              <h3>3. Preview & Import</h3>
              <div className="status-indicator">
                <p>✅ Account selected: {accounts.find(a => a.id === selectedAccountId)?.name}</p>
                <p>✅ PDF uploaded: {pdfFile.name}</p>
              </div>
              <div className="preview-actions">
                <button
                  onClick={handlePreview}
                  disabled={isProcessing}
                  className="btn btn-secondary"
                >
                  {isProcessing ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      Processing PDF...
                    </>
                  ) : (
                    '🔍 Preview Transactions'
                  )}
                </button>
              </div>

              {extractionResult && (
                <div className="extraction-info">
                  <h4>📊 Extraction Results</h4>
                  <div className="extraction-stats">
                    <div className="stat">
                      <span className="stat-label">Type:</span>
                      <span className="stat-value">
                        {extractionResult.isScanned ? 'Scanned PDF (OCR)' : 'Text-based PDF'}
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Confidence:</span>
                      <span className="stat-value">
                        {Math.round(extractionResult.confidence * 100)}%
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Transactions Found:</span>
                      <span className="stat-value">{extractionResult.transactionCount}</span>
                    </div>
                  </div>

                  {/* QC Warnings */}
                  {qcResult && qcResult.warnings.length > 0 && (
                    <div className="qc-warnings">
                      <h4 className="qc-title">Quality Control Results</h4>
                      <div className="qc-confidence">
                        <span className={`confidence-badge confidence-${qcResult.confidence}`}>
                          {qcResult.confidence.toUpperCase()} CONFIDENCE
                        </span>
                      </div>
                      {qcResult.expected_count && (
                        <div className="qc-stats">
                          <span>Expected: {qcResult.expected_count} transactions</span>
                          <span>Parsed: {qcResult.parsed_count} transactions</span>
                          {qcResult.discrepancy !== null && qcResult.discrepancy !== 0 && (
                            <span className={qcResult.discrepancy > 0 ? 'warning' : 'info'}>
                              {qcResult.discrepancy > 0 ? 'Missing' : 'Extra'}: {Math.abs(qcResult.discrepancy)} transactions
                            </span>
                          )}
                        </div>
                      )}
                      <div className="qc-warnings-list">
                        {qcResult.warnings.map((warning, index) => (
                          <div key={index} className={`qc-warning warning-${qcResult.confidence}`}>
                            {warning}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Duplicate Detection Results */}
                  {duplicateInfo && (
                    <div className="duplicate-detection">
                      <h4 className="duplicate-title">Duplicate Detection Results</h4>
                      <div className="duplicate-summary">
                        <div className="duplicate-stat">
                          <span className="stat-label">Will Import:</span>
                          <span className="stat-value import-count">{effectiveImportCount} transactions</span>
                        </div>
                        <div className="duplicate-stat">
                          <span className="stat-label">Will Skip:</span>
                          <span className="stat-value skip-count">{effectiveSkipCount} duplicates</span>
                        </div>
                      </div>
                      {effectiveSkipCount > 0 && (
                        <div className="duplicate-warning">
                          <p>⚠️ {effectiveSkipCount} transactions already exist in this account and will be skipped during import.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {extractionResult.text && (
                    <details className="extracted-text">
                      <summary>View Extracted Text (first 500 chars)</summary>
                      <pre>{extractionResult.text.substring(0, 500)}...</pre>
                    </details>
                  )}
                </div>
              )}

              {parsedTransactions.length > 0 && (
                <div className="side-by-side-preview">
                  <h4>💳 PDF Preview & Extracted Transactions</h4>
                  <div className="preview-container">
                    {/* PDF Viewer Side */}
                    <div className="pdf-viewer-side">
                      <div className="pdf-viewer-header">
                        <h5>📄 Original PDF</h5>
                        <div className="pdf-controls">
                          {/* Zoom Controls */}
                          <div className="zoom-controls">
                            <button 
                              onClick={() => setScale(Math.max(0.5, scale - 0.2))}
                              disabled={scale <= 0.5}
                              className="nav-btn"
                              title="Zoom Out"
                            >
                              −
                            </button>
                            <span className="zoom-info">
                              {Math.round(scale * 100)}%
                            </span>
                            <button 
                              onClick={() => setScale(Math.min(3.0, scale + 0.2))}
                              disabled={scale >= 3.0}
                              className="nav-btn"
                              title="Zoom In"
                            >
                              +
                            </button>
                          </div>
                          
                          {/* Page Navigation */}
                          {numPages && (
                            <div className="pdf-navigation">
                              <button 
                                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                                disabled={pageNumber <= 1}
                                className="nav-btn"
                                title="Previous Page"
                              >
                                ←
                              </button>
                              <span className="page-info">
                                Page {pageNumber} of {numPages}
                              </span>
                              <button 
                                onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                                disabled={pageNumber >= numPages}
                                className="nav-btn"
                                title="Next Page"
                              >
                                →
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="pdf-viewer-container">
                        {pdfDataUrl && (
                          <div className="pdf-fallback">
                            <Document
                              file={pdfDataUrl}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={onDocumentLoadError}
                              loading={<div className="pdf-loading">Loading PDF...</div>}
                              error={
                                <div className="pdf-error">
                                  <p>PDF viewer not available - using fallback</p>
                                  <iframe 
                                    src={pdfDataUrl}
                                    width={Math.round(600 * scale)}
                                    height={Math.round(700 * scale)}
                                    style={{ border: '1px solid #ccc', maxWidth: '100%', maxHeight: '100%' }}
                                    title="PDF Preview"
                                  />
                                </div>
                              }
                            >
                              <Page 
                                pageNumber={pageNumber} 
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                              />
                            </Document>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transactions List Side */}
                    <div className="transactions-side">
                      <div className="transactions-header">
                        <h5>💰 Extracted Transactions ({parsedTransactions.length})</h5>
                      </div>
                      <div className="transactions-list">
                        {parsedTransactions.map((transaction, index) => {
                          const matchDetail = duplicateInfo?.matchMap[index];
                          const isDuplicate = !!matchDetail;
                          const isOverridden = overriddenIndices.has(index);
                          const showAsDuplicate = isDuplicate && !isOverridden;

                          const toggleOverride = () => {
                            setOverriddenIndices(prev => {
                              const next = new Set(prev);
                              if (next.has(index)) next.delete(index);
                              else next.add(index);
                              return next;
                            });
                          };

                          return (
                            <div key={index} className={`transaction-card ${showAsDuplicate ? 'duplicate-transaction' : ''}`}>
                              <div className="transaction-main">
                                <div className="transaction-date">
                                  {transaction.date}
                                </div>
                                <div className="transaction-description">
                                  {transaction.description}
                                </div>
                                <div className={`transaction-amount ${transaction.amount < 0 ? 'negative' : 'positive'}`}>
                                  ${Math.abs(transaction.amount).toFixed(2)}
                                </div>
                              </div>
                              <div className="transaction-meta">
                                {transaction.reference && (
                                  <span className="transaction-ref">#{transaction.reference}</span>
                                )}
                                {showAsDuplicate && matchDetail && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span className="duplicate-badge">⚠️ Duplicate of:</span>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                                      {formatMatchDate(matchDetail.date)}
                                      {' · '}
                                      {matchDetail.description.length > 40
                                        ? matchDetail.description.slice(0, 40) + '…'
                                        : matchDetail.description}
                                      {' · '}
                                      ${Math.abs(matchDetail.amount).toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {isDuplicate && (
                                  <button
                                    onClick={toggleOverride}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: 'var(--text-muted)',
                                      fontSize: '0.72rem',
                                      padding: '2px 0',
                                      textDecoration: 'underline',
                                      textUnderlineOffset: '2px',
                                      marginTop: '2px',
                                      alignSelf: 'flex-start',
                                    }}
                                  >
                                    {isOverridden ? '← Skip this one' : 'Import anyway →'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {parsedTransactions.length > 0 && (
                <div className="import-actions">
                  <button
                    onClick={handleImport}
                    disabled={isImporting}
                    className="btn btn-primary"
                  >
                    {isImporting ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Importing...
                      </>
                    ) : (
                      `🚀 Import ${effectiveImportCount} Transactions`
                    )}
                  </button>
                  {effectiveSkipCount > 0 && (
                    <p className="import-note">
                      {effectiveSkipCount} duplicates will be skipped
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={handleClose} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>

      {/* Conflict Resolution Modal */}
      <CategorizationConflictModal
        isOpen={conflictModalOpen}
        onClose={() => setConflictModalOpen(false)}
        conflicts={conflicts}
        onResolve={handleConflictResolve}
        categories={categories}
      />
    </div>
  );
};

export default PDFImportModal;
